import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { TextArea, RadioGroup } from '../../../../components/workbook/forms';

export default function Worksheet2_1Page() {
  const [assumptions, setAssumptions] = useState<string[]>(['', '', '']);
  const [snippet1Label, setSnippet1Label] = useState('');
  const [snippet2Label, setSnippet2Label] = useState('');
  const [snippet3Label, setSnippet3Label] = useState('');

  const evidenceOptions = [
    { value: 'anecdote', label: 'Anecdote' },
    { value: 'directional', label: 'Directional' },
    { value: 'strong', label: 'Strong' },
    { value: 'verified', label: 'Verified' }
  ];

  return (
    <div className="min-h-screen bg-[var(--bg-raised)]">
      <header className="bg-[var(--slate-dim)] border-b border-[var(--slate)]">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link
            to="/elevation/workbook/oqf/module-2"
            className="text-[var(--text-sec)] hover:text-[var(--text-pri)] text-sm mb-2 inline-flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Module 2
          </Link>
          <h1 className="text-2xl font-bold text-[var(--text-pri)]">
            ✍️ Activity: Worksheets 2.1 & 2.2
          </h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-6 py-8">
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">
            Worksheet 2.1: Identify the Assumptions
          </h2>
          <p className="text-[var(--text-sec)] mb-4">
            Based on the "Pro Tier" scenario, what are the 2-3 core assumptions the team is making <strong>without</strong> strong evidence?
          </p>
          <div className="space-y-4">
            {assumptions.map((assumption, index) => (
              <div key={index}>
                <TextArea
                  label={index === 0 ? 'Assumption 1 (e.g., The exec team believes...)' : 
                         index === 1 ? 'Assumption 2 (e.g., The marketing team believes...)' :
                         'Assumption 3 (e.g., The engineering team believes...)'}
                  value={assumption}
                  onChange={(e) => {
                    const updated = [...assumptions];
                    updated[index] = e.target.value;
                    setAssumptions(updated);
                  }}
                  placeholder="Enter the assumption..."
                  rows={2}
                />
              </div>
            ))}
          </div>
        </div>

        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] p-6 mb-6">
          <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-4">
            Worksheet 2.2: Label the Evidence
          </h2>
          <p className="text-[var(--text-sec)] mb-4">
            You hear these statements in a follow-up meeting. Read each one and circle the correct evidence quality label.
          </p>

          <div className="space-y-6">
            <div className="bg-[var(--surface)] p-4">
              <h3 className="font-semibold text-[var(--text-pri)] mb-2">Snippet 1:</h3>
              <blockquote className="text-[var(--text-sec)] italic mb-4 border-l-4 border-[var(--rule)] pl-4">
                "The CEO says, 'I was talking to our biggest customer last week, and he said he'd <em>definitely</em> pay more for a Pro tier.'"
              </blockquote>
              <RadioGroup
                label="Label:"
                options={evidenceOptions}
                value={snippet1Label}
                onChange={setSnippet1Label}
              />
            </div>

            <div className="bg-[var(--surface)] p-4">
              <h3 className="font-semibold text-[var(--text-pri)] mb-2">Snippet 2:</h3>
              <blockquote className="text-[var(--text-sec)] italic mb-4 border-l-4 border-[var(--rule)] pl-4">
                "The CMO says, 'Our last campaign doubled impressions—people clearly just need to see the product more.'"
              </blockquote>
              <RadioGroup
                label="Label:"
                options={evidenceOptions}
                value={snippet2Label}
                onChange={setSnippet2Label}
              />
            </div>

            <div className="bg-[var(--surface)] p-4">
              <h3 className="font-semibold text-[var(--text-pri)] mb-2">Snippet 3:</h3>
              <blockquote className="text-[var(--text-sec)] italic mb-4 border-l-4 border-[var(--rule)] pl-4">
                "The sprint report shows 42% of engineering hours are spent reworking unclear requirements from Product, delaying new development."
              </blockquote>
              <RadioGroup
                label="Label:"
                options={evidenceOptions}
                value={snippet3Label}
                onChange={setSnippet3Label}
              />
            </div>
          </div>
        </div>

        <div className="bg-[var(--green-dim)] border border-[var(--green)] p-4 mb-6">
          <h3 className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)] mb-2">✓ Model Answers</h3>
          <div className="text-sm text-[var(--green)] dark:text-[var(--text-ter)] space-y-2">
            <p><strong>Snippet 1:</strong> Anecdote - One person's opinion</p>
            <p><strong>Snippet 2:</strong> Directional - Real metric, but it's a proxy</p>
            <p><strong>Snippet 3:</strong> Strong - Multiple data sources (sprint report + quantitative metrics)</p>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <Link
            to="/elevation/workbook/oqf/module-2"
            className="px-4 py-2 border border-[var(--rule)] text-[var(--text-sec)] hover:bg-[var(--surface)] inline-flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Link>
          <Link
            to="/elevation/workbook/oqf/module-3"
            className="px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] inline-flex items-center gap-2"
          >
            Next: Module 3
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}






